/* Cart Drawer Scroll Bar — behavior
   Handles: pin-aware shuffle on cart open, wheel-style looping arrow
   navigation, smooth scroll.
*/

(function () {
  function shuffle(array) {
    // Fisher-Yates
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  function arrangePlaques(container) {
    const track = container.querySelector('[data-cdsb-track]');
    if (!track) return;

    const order = container.dataset.order || 'random';
    const plaques = Array.from(track.querySelectorAll('[data-cdsb-plaque]'));
    if (plaques.length === 0) return;

    // In Theme Editor, always keep the authored order so blocks stay put
    // while editing — shuffling only happens on the live storefront.
    const inThemeEditor = window.Shopify && window.Shopify.designMode;

    if (order === 'fixed' || inThemeEditor) {
      return;
    }

    // Separate pinned (max 3) from unpinned, preserve pin order as authored
    const pinned = plaques.filter((p) => p.dataset.pin === 'true');
    const unpinned = plaques.filter((p) => p.dataset.pin !== 'true');

    const pinnedFinal = pinned.slice(0, 3);
    const shuffledRest = shuffle(unpinned.slice());

    const finalOrder = pinnedFinal.concat(shuffledRest);

    finalOrder.forEach((plaque) => track.appendChild(plaque));
  }

  function setupDots(container) {
    const track = container.querySelector('[data-cdsb-track]');
    const dotsWrap = container.querySelector('[data-cdsb-dots]');
    if (!track || !dotsWrap) return;

    const plaques = Array.from(track.querySelectorAll('[data-cdsb-plaque]'));
    if (plaques.length === 0) {
      dotsWrap.innerHTML = '';
      return;
    }

    dotsWrap.innerHTML = '';
    plaques.forEach((plaque, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'cdsb__dot';
      dot.setAttribute('aria-label', 'Go to item ' + (index + 1));
      dot.addEventListener('click', () => {
        plaque.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      });
      dotsWrap.appendChild(dot);
    });

    function updateActiveDot() {
      const trackRect = track.getBoundingClientRect();
      let closestIndex = 0;
      let closestDistance = Infinity;
      plaques.forEach((plaque, index) => {
        const rect = plaque.getBoundingClientRect();
        const distance = Math.abs(rect.left - trackRect.left);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      const dots = dotsWrap.querySelectorAll('.cdsb__dot');
      dots.forEach((dot, index) => {
        dot.classList.toggle('cdsb__dot--active', index === closestIndex);
      });
    }

    let scrollTimer;
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(updateActiveDot, 80);
    });
    updateActiveDot();
  }

  function setupArrows(container) {
    const track = container.querySelector('[data-cdsb-track]');
    const prevBtn = container.querySelector('[data-cdsb-prev]');
    const nextBtn = container.querySelector('[data-cdsb-next]');
    if (!track || !prevBtn || !nextBtn) return;

    function scrollByPlaques(direction) {
      const plaque = track.querySelector('[data-cdsb-plaque]');
      if (!plaque) return;
      const plaqueWidth = plaque.getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(track).gap) || 8;
      const step = plaqueWidth + gap;
      track.scrollBy({ left: direction * step, behavior: 'smooth' });
    }

    function updateArrowState() {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const tolerance = 4;
      prevBtn.disabled = track.scrollLeft <= tolerance;
      nextBtn.disabled = track.scrollLeft >= maxScroll - tolerance;
    }

    prevBtn.addEventListener('click', () => scrollByPlaques(-1));
    nextBtn.addEventListener('click', () => scrollByPlaques(1));
    track.addEventListener('scroll', updateArrowState);
    window.addEventListener('resize', updateArrowState);
    updateArrowState();
  }

  function initScrollBar(container) {
    arrangePlaques(container);
    setupArrows(container);
    setupDots(container);
  }

  // Some themes render the cart drawer more than once (separate markup for
  // mobile/desktop breakpoints). A plain querySelector always grabs the
  // first one in the DOM, which can be the hidden, inactive copy — pick the
  // anchor whose cart-drawer ancestor is actually the one in use for the
  // current viewport (not display:none) instead of just the first match.
  function getActiveAnchor() {
    var anchors = Array.from(document.querySelectorAll('[data-cdsb-anchor]'));
    if (anchors.length === 0) return null;
    if (anchors.length === 1) return anchors[0];
    for (var i = 0; i < anchors.length; i++) {
      var cd = anchors[i].closest('cart-drawer');
      if (cd && window.getComputedStyle(cd).display !== 'none') {
        return anchors[i];
      }
    }
    return anchors[0];
  }

  var savedTemplateNode = null;

  function insertAfterAnchor(anchor, node) {
    anchor.parentNode.insertBefore(node, anchor.nextSibling);
    var newSection = node.matches('[data-cdsb]') ? node : node.querySelector('[data-cdsb]');
    if (newSection) {
      // Only reveal it once it's actually inside the cart — it starts
      // hidden (display:none, set inline in the Liquid) precisely so it
      // never appears "orphaned" on the page (e.g. below the footer) when
      // there's nowhere to relocate it to, such as an empty cart with no
      // anchor rendered at all.
      newSection.style.display = '';
      fullBleedFix(newSection);
      initScrollBar(newSection);
    }
  }

  function relocateIntoCartDrawer() {
    var anchor = getActiveAnchor();
    if (!anchor) {
      // No anchor anywhere on the page right now (e.g. cart is empty, so
      // cart-drawer.liquid didn't render the anchor at all) — make sure any
      // existing section instance stays hidden rather than showing wherever
      // theme.liquid happens to have placed it.
      var orphan = document.querySelector('[data-cdsb]');
      if (orphan) orphan.style.display = 'none';
      return;
    }

    var section = document.querySelector('[data-cdsb]');

    // If our block isn't present at all right now, it was likely wiped out
    // by the theme replacing the cart drawer's innerHTML wholesale (e.g.
    // after adding another item) — the original node rendered by
    // theme.liquid only exists once and is gone for good once that
    // happens. Restore it from our saved backup instead.
    if (!section) {
      if (savedTemplateNode) {
        insertAfterAnchor(anchor, savedTemplateNode.cloneNode(true));
      }
      return;
    }

    // find the Shopify section wrapper div so we move the whole rendered block, not just the inner element
    var wrapper = section.closest('[id^="shopify-section-"]') || section;
    // Shopify's default section wrapper carries page-section spacing (margin/
    // padding/max-width) meant for full-page sections — reset it so our block
    // can span the full width of the narrow cart drawer instead.
    wrapper.style.margin = '0';
    wrapper.style.padding = '0';
    wrapper.style.maxWidth = 'none';
    wrapper.style.width = '100%';

    // Keep a pristine backup the first time we see it, before it's ever
    // moved or potentially destroyed by a later cart-drawer refresh. Clone
    // BEFORE we reveal it, so the saved copy also starts hidden and gets
    // the same "reveal on successful insert" treatment when restored.
    if (!savedTemplateNode) {
      savedTemplateNode = wrapper.cloneNode(true);
    }

    if (wrapper.previousElementSibling !== anchor && anchor.nextElementSibling !== wrapper) {
      insertAfterAnchor(anchor, wrapper);
    } else {
      section.style.display = '';
      fullBleedFix(section);
    }
  }

  function fullBleedFix(container) {
    // The actual left/right inset comes from the drawer's own inner content
    // padding (.drawer__inner), not from our section — measure it and cancel
    // it out with matching negative margins so our block reaches true edges,
    // the same way David's competitor block does.
    var drawerInner = container.closest('.drawer__inner');
    if (!drawerInner) return;
    var style = window.getComputedStyle(drawerInner);
    var padLeft = parseFloat(style.paddingLeft) || 0;
    var padRight = parseFloat(style.paddingRight) || 0;
    if (padLeft === 0 && padRight === 0) return;
    container.style.marginLeft = '-' + padLeft + 'px';
    container.style.marginRight = '-' + padRight + 'px';
    container.style.width = 'calc(100% + ' + (padLeft + padRight) + 'px)';
    container.style.boxSizing = 'border-box';
  }

  function bootstrap() {
    relocateIntoCartDrawer();
    initAll();
    startVisibilityWatcher();
    startDrawerObserver();
  }

  // DOMContentLoaded may have already fired before this script runs — this
  // can happen on pages loaded via speculative prerendering/instant
  // navigation, where scripts execute during a background prerender pass
  // rather than at the moment the visitor actually sees the page. Checking
  // readyState directly (instead of only listening for the event) makes
  // sure we still bootstrap correctly in that case.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // If this page was prerendered in the background and only just became the
  // active page, explicitly re-run everything — the DOM (and cart state) at
  // prerender time may not match the DOM once the page is actually shown.
  document.addEventListener('prerenderingchange', bootstrap);

  // Covers back/forward-cache restores and other "page became visible again
  // without a fresh script execution" cases.
  window.addEventListener('pageshow', bootstrap);

  var lastInitTime = 0;
  function initAll() {
    var now = Date.now();
    if (now - lastInitTime < 300) return; // ignore rapid duplicate triggers
    lastInitTime = now;
    document.querySelectorAll('[data-cdsb]').forEach(initScrollBar);
  }

  // Some theme events (like hovering the cart icon for prefetch) can look
  // like a "cart opened" signal without the drawer actually becoming
  // visible. Instead of trusting event names, watch the drawer's real
  // computed visibility and only reshuffle on an actual closed→open
  // transition, so hovering near the block can never trigger a reshuffle.
  var wasVisible = false;
  var watcherStarted = false;
  function startVisibilityWatcher() {
    if (watcherStarted) return;
    watcherStarted = true;
    setInterval(function () {
      var anchor = getActiveAnchor();
      var drawer = anchor ? anchor.closest('cart-drawer') : document.querySelector('cart-drawer');
      if (!drawer) return;
      var isVisible = window.getComputedStyle(drawer).visibility !== 'hidden';
      if (isVisible && !wasVisible) {
        relocateIntoCartDrawer();
        initAll();
      }
      wasVisible = isVisible;
    }, 250);
  }

  // The theme fully replaces the cart drawer's innerHTML on every cart
  // change (add item, change qty, remove item), which wipes out our block
  // instantly, regardless of whether the drawer stays visible throughout —
  // the visibility watcher alone can't catch that case. Watch the drawer's
  // actual content for changes and restore the block the moment it's gone
  // (or hide it again the moment the cart becomes empty and the anchor
  // disappears).
  //
  // IMPORTANT: this only calls relocateIntoCartDrawer() — NOT initAll().
  // relocateIntoCartDrawer() already reshuffles internally, but only in the
  // genuine "our block is missing and needs restoring" branch; when the
  // block is already correctly in place it's a no-op. initAll() has no such
  // guard — it unconditionally reshuffles every scroll bar instance on the
  // page. With other cart-drawer sections (progress bar, upsell banner)
  // also periodically touching the DOM (e.g. rebuilding their own nav dots
  // every few seconds), any of those unrelated mutations was tripping this
  // observer and, via initAll(), silently reshuffling the plaques — even
  // though nothing about the scroll bar itself had changed. The intended
  // behavior is: reshuffle only on an actual cart close→open cycle (handled
  // by the visibility watcher above), not on every incidental DOM change
  // elsewhere in the drawer while it stays open.
  var observerStarted = false;
  function startDrawerObserver() {
    if (observerStarted) return;
    var drawers = Array.from(document.querySelectorAll('cart-drawer'));
    if (drawers.length === 0) return;
    observerStarted = true;
    var debounceTimer;
    var observer = new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        relocateIntoCartDrawer();
      }, 120);
    });
    drawers.forEach(function (drawer) {
      observer.observe(drawer, { childList: true, subtree: true });
    });
  }

  // Also re-init whenever Shopify re-renders the section in the theme editor,
  // and auto-open the cart drawer so the section isn't shown "orphaned" on
  // the page — Theme Editor otherwise renders hidden/closed-drawer sections
  // inline on the page instead of inside the drawer overlay.
  function openCartDrawerIfPresent() {
    var anchor = getActiveAnchor();
    var drawer = anchor ? anchor.closest('cart-drawer') : document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  document.addEventListener('shopify:block:select', function (event) {
    const plaque = event.target.closest('[data-cdsb-plaque]');
    if (!plaque) return;
    openCartDrawerIfPresent();
    plaque.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  });

  document.addEventListener('shopify:section:load', function (event) {
    const container = event.target.querySelector('[data-cdsb]');
    if (container) {
      initScrollBar(container);
      openCartDrawerIfPresent();
    }
  });

  document.addEventListener('shopify:section:select', function (event) {
    const container = event.target.querySelector && event.target.querySelector('[data-cdsb]');
    if (container) {
      openCartDrawerIfPresent();
    }
  });
})();